import {useMemo} from 'react'

import {ChangeableFieldsOptionsDto} from '../../options/types'
import {useOrderConfirmationChangeableFieldsRules} from '../../orderConfirmation/rules/useOrderConfirmationChangeableFieldsRules'
import {OPTION_PATH_TO_FIELDNAME_MAPPING} from '../mapping/changeableFieldsMapping'
import {mapChangeableFieldsConfiguration} from '../mapping/mapChangeableFieldsConfiguration'
import {useBaseChangeableFieldsRules} from '../rules/useBaseChangeableFieldsRules'
import {useMaterialChangeableFieldsRules} from '../rules/useMaterialFieldsRules'
import {useSpacingChangeableFieldsRules} from '../rules/useSpacingChangeableFieldsRules'
import {ChangeableFields, ConfirmationState} from '../types'

import {ChangeableFieldContextProps} from './ChangeableFieldsContext'

export interface ChangeableFieldsStateOptions {
  changeableFieldsOptions: ChangeableFieldsOptionsDto
  /** Override refresh interval for testing (default: 60s) */
  refreshIntervalMs?: number
}

export const useChangeableFieldsState = ({
  changeableFieldsOptions,
  refreshIntervalMs
}: ChangeableFieldsStateOptions) => {
  // Transform API options to internal field configurations
  // This mapping converts the DTO structure to our internal field representation
  const changeableFieldsConfiguration = useMemo(
    () =>
      mapChangeableFieldsConfiguration(changeableFieldsOptions, OPTION_PATH_TO_FIELDNAME_MAPPING),
    [changeableFieldsOptions]
  )

  // Apply base time-based changeability rules
  const baseChangeableFields = useBaseChangeableFieldsRules(
    changeableFieldsConfiguration,
    refreshIntervalMs
  )

  // Apply field-specific business rules (Order Change rules only)
  let orderChangeRulesOnlyChangeableFields = baseChangeableFields
  orderChangeRulesOnlyChangeableFields = useSpacingChangeableFieldsRules(
    orderChangeRulesOnlyChangeableFields
  )
  orderChangeRulesOnlyChangeableFields = useMaterialChangeableFieldsRules(
    orderChangeRulesOnlyChangeableFields
  )
  orderChangeRulesOnlyChangeableFields = useOrderConfirmationChangeableFieldsRules(
    orderChangeRulesOnlyChangeableFields
  )

  // This is the intermediate state - after Order Change rules but before slot management
  // Slot management rules will be applied later in OrderChangeContextProvider

  // Calculate the overall confirmation state based on individual field states
  // If any field requires dispatch confirmation, the entire form needs confirmation
  return useMemo<ChangeableFieldContextProps>(
    () => ({
      baseChangeableFields,
      orderChangeRulesOnlyChangeableFields,
      changeableFields: orderChangeRulesOnlyChangeableFields, // Will be overridden by provider with slot rules
      confirmationState: calculateConfirmationState(orderChangeRulesOnlyChangeableFields)
    }),
    [baseChangeableFields, orderChangeRulesOnlyChangeableFields]
  )
}

export const calculateConfirmationState = (
  adjustedChangeableFields: ChangeableFields
): ConfirmationState =>
  adjustedChangeableFields
    .filter((x) => x.isDirty)
    .some((x) => x.state === 'requireDispatchConfirmation')
    ? 'requireDispatchConfirmation'
    : 'autoConfirmable'
